import { REFRESH_HTTP_TIMEOUT_MS, tryRefreshToken } from '@/lib/api/client'

/**
 * Self-healing EventSource wrapper for always-on screens (packing station,
 * office speaker, customer display). Plain EventSource dies permanently in
 * three real-world cases this wrapper recovers from:
 *
 * - `auth_expired`: the server kills every SSE stream once the access token
 *   from the ORIGINAL request expires (SSE headers are frozen at connect).
 *   Recovery: silent refresh, then reconnect so the new request carries a
 *   fresh cookie. On refresh failure keep retrying forever with capped
 *   backoff — unattended machines must self-recover once a valid session
 *   reappears (e.g. re-login from another tab).
 * - Silent drops (NAT timeout, wifi switch, sleep/wake) where `onerror`
 *   never fires: a watchdog reconnects when nothing — including the server's
 *   25s `heartbeat` events — arrives within the window.
 * - Wake-up: `online` / tab-visible events skip any pending backoff.
 *
 * Concurrency invariant: at most ONE live connection, ever. Each connect()
 * bumps a generation counter; every handler belongs to one generation and
 * no-ops when it is stale. A stray connection would double-announce every
 * order on the station speaker, so this is load-bearing, not defensive.
 */

type EventHandler = (ev: MessageEvent) => void

export interface ResilientEventSourceOptions {
    url: string
    /** Named SSE events to forward. Do NOT register `auth_expired`/`heartbeat` — the wrapper owns those. */
    listeners: Record<string, EventHandler>
    onConnectedChange?: (connected: boolean) => void
    /** Override for tests. Default 60s = 2 missed server heartbeats + margin. */
    watchdogMs?: number
}

export interface ResilientEventSourceHandle {
    close: () => void
}

const DEFAULT_WATCHDOG_MS = 60_000
const ERROR_BACKOFF_CAP_MS = 60_000
// ~1 retry/min: a backend-restart window equals missed (unreplayable) order
// announcements, so always-on machines must probe frequently forever.
const REFRESH_RETRY_CAP_MS = 60_000
// A refresh that outlives this is treated as failed — a hung request (TCP
// half-open after sleep, captive portal) must never wedge recovery. Derived
// from the HTTP-layer timeout so it can never fall below it: were this
// deadline the shorter one, every "retry" would await the same deduped
// in-flight request and recovery would stand still while appearing to retry.
const REFRESH_TIMEOUT_MS = REFRESH_HTTP_TIMEOUT_MS + 5_000
// Floor between a successful refresh and the reconnect. The server currently
// caps auth_expired at one per revalidation tick, but the client must not
// depend on that: without a floor, a server that validated on connect would
// turn recovery into a hot loop hammering /auth/refresh from an unattended
// kiosk.
const AUTH_RECONNECT_FLOOR_MS = 1_000

export function createResilientEventSource(
    options: ResilientEventSourceOptions,
): ResilientEventSourceHandle {
    const { url, listeners, onConnectedChange } = options
    const watchdogMs = options.watchdogMs ?? DEFAULT_WATCHDOG_MS

    // Every source ever opened and not yet closed, so close() can silence
    // strays even if an invariant is broken elsewhere.
    const sources = new Set<EventSource>()
    let gen = 0
    let es: EventSource | null = null
    let closed = false
    let connected = false
    let retryCount = 0
    let refreshRetryCount = 0
    // True from "cookie suspected dead" until a refresh succeeds. While set,
    // every reconnect path must go through recoverAuth, never straight to
    // connect() — a connect with a dead cookie just 401-loops.
    let authMode = false
    let refreshInFlight: Promise<boolean> | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null

    const setConnected = (value: boolean) => {
        if (connected === value) return
        connected = value
        onConnectedChange?.(value)
    }

    const clearReconnectTimer = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer)
        reconnectTimer = null
    }

    const clearWatchdogTimer = () => {
        if (watchdogTimer) clearTimeout(watchdogTimer)
        watchdogTimer = null
    }

    const dropSource = (source: EventSource) => {
        source.close()
        sources.delete(source)
        if (es === source) {
            es = null
            setConnected(false)
        }
    }

    const errorBackoff = () =>
        Math.min(1000 * Math.pow(2, retryCount), ERROR_BACKOFF_CAP_MS)

    const scheduleReconnect = (delayMs: number) => {
        if (closed) return
        clearReconnectTimer()
        reconnectTimer = setTimeout(connect, delayMs)
    }

    // Never rejects, never hangs: rejection and timeout both mean "failed
    // attempt", so the retry loop always keeps moving.
    const attemptRefresh = () =>
        new Promise<boolean>((resolve) => {
            const deadline = setTimeout(() => resolve(false), REFRESH_TIMEOUT_MS)
            tryRefreshToken().then(
                (v) => {
                    clearTimeout(deadline)
                    resolve(v)
                },
                () => {
                    clearTimeout(deadline)
                    resolve(false)
                },
            )
        })

    // Serialized: concurrent triggers share one in-flight refresh, and only
    // the first resolver observes authMode still set and reconnects.
    const recoverAuth = async () => {
        authMode = true
        if (!refreshInFlight) {
            refreshInFlight = attemptRefresh().finally(() => {
                refreshInFlight = null
            })
        }
        const refreshed = await refreshInFlight
        if (closed || !authMode) return
        if (refreshed) {
            authMode = false
            refreshRetryCount = 0
            scheduleReconnect(AUTH_RECONNECT_FLOOR_MS)
            return
        }
        refreshRetryCount++
        const delay = Math.min(
            1000 * Math.pow(2, refreshRetryCount),
            REFRESH_RETRY_CAP_MS,
        )
        clearReconnectTimer()
        reconnectTimer = setTimeout(() => void recoverAuth(), delay)
    }

    function connect() {
        if (closed) return
        clearReconnectTimer()
        if (es) dropSource(es)
        gen++
        const myGen = gen

        let source: EventSource
        try {
            source = new EventSource(url, { withCredentials: true })
        } catch {
            retryCount++
            scheduleReconnect(errorBackoff())
            return
        }
        es = source
        sources.add(source)
        const stale = () => closed || myGen !== gen || es !== source

        // Any traffic proves liveness; silence past the window means the TCP
        // connection died without the browser ever firing onerror.
        const armWatchdog = () => {
            clearWatchdogTimer()
            watchdogTimer = setTimeout(() => {
                if (stale()) return
                dropSource(source)
                connect()
            }, watchdogMs)
        }
        armWatchdog()

        source.onopen = () => {
            if (stale()) return
            retryCount = 0
            setConnected(true)
            armWatchdog()
        }

        source.onerror = () => {
            const wasStale = stale()
            dropSource(source)
            if (wasStale) return
            clearWatchdogTimer()
            retryCount++
            const delay = errorBackoff()
            // Persistent failures may be plain 401s from an expired cookie
            // (stream died silently during sleep — no auth_expired was ever
            // received). Route recovery through the session refresh; it only
            // reconnects once the refresh actually succeeds.
            if (retryCount >= 2) {
                clearReconnectTimer()
                reconnectTimer = setTimeout(() => void recoverAuth(), delay)
                return
            }
            scheduleReconnect(delay)
        }

        source.addEventListener('heartbeat', () => {
            if (stale()) return
            armWatchdog()
        })

        // The frozen-cookie stream is unrecoverable server-side; only a new
        // request can carry the refreshed cookie.
        source.addEventListener('auth_expired', () => {
            const wasStale = stale()
            dropSource(source)
            if (wasStale) return
            clearWatchdogTimer()
            void recoverAuth()
        })

        for (const [type, handler] of Object.entries(listeners)) {
            source.addEventListener(type, (event) => {
                if (stale()) return
                retryCount = 0
                armWatchdog()
                handler(event)
            })
        }
    }

    const reconnectNowIfDown = () => {
        if (closed || connected) return
        clearReconnectTimer()
        if (authMode) {
            // Skip the capped wait and retry the refresh immediately.
            refreshRetryCount = 0
            void recoverAuth()
            return
        }
        if (!es) connect()
    }

    const onOnline = () => reconnectNowIfDown()
    const onVisible = () => {
        if (document.visibilityState === 'visible') reconnectNowIfDown()
    }

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)

    connect()

    return {
        close: () => {
            closed = true
            window.removeEventListener('online', onOnline)
            document.removeEventListener('visibilitychange', onVisible)
            clearReconnectTimer()
            clearWatchdogTimer()
            for (const source of sources) source.close()
            sources.clear()
            es = null
            setConnected(false)
        },
    }
}
